/**
 * @file `NavAuthIsland` — the auth-aware slot in www's top nav.
 * @module @labcraft/www/ui/islands/nav-auth
 *
 * Every page's nav carries this island in the right cluster. The
 * static render (and the client's first paint — hydration parity) is
 * the anonymous, public-beta CTA (`Start your first lab →`); after mount
 * it reads `/api/account/me` + `/api/config` through the shared
 * {@link IslandRuntime} and, for a signed-in learner, swaps in the
 * shared `AccountMenu` (sign out + update-account hop to lab). An
 * anonymous visitor keeps the anchor, but its label switches to the
 * open-enrollment copy (`WAITLIST_COPY.off.navCta`) when the waitlist
 * is off.
 *
 * The mobile menu's CTA stays static (anonymous copy for everyone);
 * the account menu lives in the bar on all breakpoints, matching the
 * pre-rebuild `WwwShell` behaviour.
 *
 * The anonymous CTA hops to the home page's sign-in section (`/#start`).
 * The campaign that drove the visit is no longer forwarded across this
 * hop — first-touch acquisition is captured to localStorage on page boot
 * (`islands/main.tsx` → `BrowserAcquisition.captureFirstTouch`) and read
 * at sign-in, so a bare `/#start` is correct.
 */

import { useEffect, useState, type JSX } from 'react'
import { AccountMenu } from '@labcraft/ui'
import type { AccountLearner } from '../../client/account.ts'
import { DEFAULT_LAB_ORIGIN, IslandRuntime } from './runtime.ts'
import { WAITLIST_COPY } from '../waitlist_copy.ts'
import styles from '../top_nav.module.css'

/**
 * The nav auth island. Takes no props — everything it varies on is
 * fetched after mount.
 *
 * @returns The CTA anchor (anonymous) or the shared account menu.
 */
export function NavAuthIsland(): JSX.Element {
  const [learner, setLearner] = useState<AccountLearner | null>(null)
  const [labOrigin, setLabOrigin] = useState(DEFAULT_LAB_ORIGIN)
  // Public beta is the cold-load assumption + the config fallback; if
  // waitlist mode is re-enabled, the runtime config swaps the label back.
  const [waitlistEnabled, setWaitlistEnabled] = useState(false)

  useEffect(() => {
    let cancelled = false
    void IslandRuntime.me().then(res => {
      if (!cancelled && res.learner) setLearner(res.learner)
    })
    void IslandRuntime.config().then(config => {
      if (cancelled) return
      setLabOrigin(config.labOrigin)
      setWaitlistEnabled(config.waitlistEnabled)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!learner) {
    return (
      <a className={`${styles.navCta} ${styles.navCtaDesktop}`} href="/#start">
        {WAITLIST_COPY[waitlistEnabled ? 'on' : 'off'].navCta}
      </a>
    )
  }

  // The signout response flips the session server-side; the full
  // reload drops any signed-in-only state on the static page.
  async function handleSignOut(): Promise<void> {
    try {
      await IslandRuntime.www().account.signout()
    } finally {
      window.location.reload()
    }
  }

  return (
    <AccountMenu
      user={{
        displayName: learner.displayName,
        email: learner.email,
        avatarUrl: learner.avatarUrl,
      }}
      onSignOut={handleSignOut}
      onUpdateAccount={() =>
        window.location.assign(`${labOrigin}/account/update`)
      }
    />
  )
}
